import {
  ChartPie,
  Compass,
  GlobeHemisphereWest,
  type Icon,
  SlidersHorizontal,
  Warning,
} from "@phosphor-icons/react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "common/components/ui/Accordion";
import { Button } from "common/components/ui/Button";
import type { WorkspaceAccount } from "common/types";

import AccountInteractions from "./AccountInteractions";
import AccountProxy from "./AccountProxy";
import AccountSSI from "./AccountSSI";
import RentedBanner from "./RentedBanner";
import SalesNavigatorContract from "./SalesNavigatorContract";
import SettingsBanner from "./SettingsBanner";

import type { ReactNode } from "react";

interface PanelItem {
  icon: Icon;
  label: string;
  component: ReactNode;
}

interface AccordionSectionProps {
  account: WorkspaceAccount;
  accountId: string | undefined;
  isLoggedIn: boolean;
  onReconnect: () => void;
}

export default function AccordionSection({
  account,
  accountId,
  isLoggedIn,
  onReconnect,
}: AccordionSectionProps) {
  const { type, sales_navigator: isSalesNavigator } = account;

  const items: PanelItem[] = [
    {
      icon: SlidersHorizontal,
      label: "Interaction limits / week",
      component: (
        <AccountInteractions
          accountId={accountId}
          isRented={type === "rental"}
        />
      ),
    },
    ...(type === "user"
      ? [
          {
            icon: ChartPie,
            label: "SSI score",
            component: isLoggedIn ? (
              <AccountSSI account={account} />
            ) : (
              <SettingsBanner
                variant="warning"
                text="Cannot show SSI for disconnected accounts"
                actionComponent={
                  <Button size="sm" onClick={onReconnect}>
                    Reconnect
                  </Button>
                }
              />
            ),
          } satisfies PanelItem,
          ...(isSalesNavigator && isLoggedIn
            ? [
                {
                  icon: Compass,
                  label: "Sales Navigator contract",
                  component: <SalesNavigatorContract accountId={account.id} />,
                } satisfies PanelItem,
              ]
            : []),
          {
            icon: GlobeHemisphereWest,
            label: "Proxy",
            component: <AccountProxy account={account} />,
          } satisfies PanelItem,
        ]
      : [
          {
            icon: Warning,
            label: "Danger zone",
            component: <RentedBanner />,
          } satisfies PanelItem,
        ]),
  ];

  return (
    <Accordion type="single" collapsible>
      {items.map(({ icon: Icon, label, component }) => (
        <AccordionItem
          key={label}
          value={label}
          className="border-b border-b-black-200 text-button-sm"
        >
          <AccordionTrigger className="px-4 py-2 gap-x-1.5">
            <Icon size={20} weight="fill" />
            {label}
          </AccordionTrigger>

          <AccordionContent className="py-3 flex flex-col gap-y-3 px-2">
            {component}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
